// LLMCalls.js
// Updated to provide user-friendly error messages for LLM rate limits (429)

import { getCurrentUserId } from './database';
import proxyapi from './ProxyApi';

const LLM_RATE_LIMIT_MESSAGE =
  'Our AI is processing too many requests right now. Please wait a minute and try again.';

export const submitInitialInstructions = async (projectId, formData) => {
  try {
    const userId = await getCurrentUserId();
    const response = await proxyapi.post('/api/v1/llm/initial-instructions', {
      userId,
      projectId,
      title: formData.title,
      instructions: formData.instructions
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error('Error submitting initial instructions:', error);
    throw error;
  }
};

export const getFirstPromptStatus = async (promptId) => {
  try {
    const response = await proxyapi.get(`/api/v1/llm/firstprompt/status/${promptId}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error('Error fetching first prompt status:', error);
    throw error;
  }
};

export const getFirstPromptResults = async (promptId) => {
  try {
    const response = await proxyapi.get(`/api/v1/llm/firstprompt/${promptId}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error('Error fetching first prompt results:', error);
    throw error;
  }
};

export const submitStructuredData = async (projectId, formData) => {
  try {
    const userId = await getCurrentUserId();
    const ui_description = createUiDescription(formData);
    const view_title = formData.title;
    const requestPayload = {
      userId,
      projectId,
      view_title,
      ui_description
    };
    if (formData.reference) {
      requestPayload.reference = {
        viewId: formData.reference.viewId,
        viewTitle: formData.reference.viewTitle,
        versionId: formData.reference.versionId,
        explanation: formData.reference.explanation
      };
    }
    const response = await proxyapi.post('/api/v1/llm/initial-instructions', requestPayload);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error('Error submitting structured data:', error);
    throw error;
  }
};

// Helper function to format the layout description
function formatLayout(formData) {
  const { layout, otherLayout, includeTopPanel, includeBottomPanel } = formData;

  let layoutDescription = '';

  switch (layout) {
    case 'single-page':
      layoutDescription = 'Single-page layout (one main area)';
      break;
    case 'left-sidebar':
      layoutDescription = 'Left sidebar with main content area';
      break;
    case 'right-sidebar':
      layoutDescription = 'Right sidebar with main content area';
      break;
    case 'split-screen':
      layoutDescription = 'Split-screen layout (left and right panels)';
      break;
    case 'other':
      layoutDescription = otherLayout && otherLayout.trim()
        ? `Other (${otherLayout.trim()})`
        : 'Other (unspecified)';
      break;
    default:
      layoutDescription = 'Main area';
  }

  // Add info about top/bottom panels if present
  const extras = [];
  if (includeTopPanel) {
    extras.push('top panel');
  }
  if (includeBottomPanel) {
    extras.push('bottom panel');
  }
  if (extras.length > 0) {
    layoutDescription += ` with ${extras.join(' and ')}`;
  }

  return layoutDescription;
}


function createUiDescription(formData) {
  let ui_description = '';

  // Always include the page title
  if (formData.title && formData.title.trim()) {
    ui_description += `Page Title: ${formData.title.trim()}`;
  }

  // Only include layout if present and not empty
  if (formData.layout && formData.layout !== 'other' && formData.layout.trim()) {
    if (formData.device === 'Desktop') {
      ui_description += `\n\nLayout of the Page: ${formatLayout(formData)}`;
    }
  } else if (formData.layout === 'other' && formData.otherLayout && formData.otherLayout.trim()) {
    ui_description += `\n\nLayout of the Page: Other (${formData.otherLayout.trim()})`;
  }

  // UI Elements section (only if components exist and are non-empty)
  if (Array.isArray(formData.components) && formData.components.length > 0) {
    ui_description += `\n\nUI Elements from top to bottom:`;
    ui_description += formatComponents(
      formData.components,
      formData.layout,
      formData.includeTopPanel,
      formData.includeBottomPanel
    );
  }

  // Interactivity (only if present and non-empty)
  if (formData.interactivity && formData.interactivity.trim()) {
    ui_description += `\n\nInteractivity:\n${formData.interactivity.trim()}`;
  }

  // Content (only if present and non-empty)
  if (formData.content && formData.content.trim()) {
    ui_description += `\n\nContent:\n${formData.content.trim()}`;
  }

  // For freestyle/wireframe flows (no components), use instructions as main content
  if (
    (!formData.components || formData.components.length === 0) &&
    formData.instructions && formData.instructions.trim()
  ) {
    // If the instructions are already included as content, don't add again
    if (!ui_description.includes(formData.instructions.trim())) {
      ui_description += `\n\n${formData.instructions.trim()}`;
    }
  }

  // Remove any accidental double line breaks
  ui_description = ui_description.replace(/\n{3,}/g, '\n\n');

  return ui_description.trim();
}

const formatComponents = (components, layout, includeTopPanel, includeBottomPanel) => {
  const sections = {};
  components.forEach((component) => {
    const section = component.section || 'main';
    if (!sections[section]) {
      sections[section] = [];
    }
    sections[section].push(component.content);
  });
  let result = '';
  const sectionOrder = getSectionOrder(layout, includeTopPanel, includeBottomPanel);
  sectionOrder.forEach((sectionKey) => {
    if (sections[sectionKey]) {
      let sectionTitle;
      switch(sectionKey) {
        case 'top':
          sectionTitle = 'FULL-WIDTH TOP PANEL';
          break;
        case 'bottom':
          sectionTitle = 'FULL-WIDTH BOTTOM PANEL';
          break;
        case 'sidebar':
          sectionTitle = 'SIDEBAR';
          break;
        case 'main':
          sectionTitle = 'MAIN AREA';
          break;
        case 'left':
          sectionTitle = 'LEFT SIDE';
          break;
        case 'right':
          sectionTitle = 'RIGHT SIDE';
          break;
        default:
          sectionTitle = sectionKey.toUpperCase();
      }
      result += `\n${sectionTitle}:\n`;
      sections[sectionKey].forEach((content, index) => {
        result += `${index + 1}. ${content}\n`;
      });
    }
  });
  return result;
};

const getSectionOrder = (layout, includeTopPanel, includeBottomPanel) => {
  let sections = [];
  if (includeTopPanel) {
    sections.push('top');
  }
  switch (layout) {
    case 'single-page':
    case 'other':
      sections.push('main');
      break;
    case 'left-sidebar':
      sections.push('sidebar', 'main');
      break;
    case 'right-sidebar':
      sections.push('main', 'sidebar');
      break;
    case 'split-screen':
      sections.push('left', 'right');
      break;
    default:
      sections.push('main');
  }
  if (includeBottomPanel) {
    sections.push('bottom');
  }
  return sections;
};

export const submitClarificationResponses = async (projectId, promptId, answeredQuestions) => {
  try {
    const userId = await getCurrentUserId();
    if (!userId || !projectId || !promptId) {
      throw new Error('Missing required parameters');
    }
    try {
      const subscriptionResponse = await proxyapi.get(`/api/v1/users/${userId}/subscription`);
      const { status, trialGenerationsLeft } = subscriptionResponse.data;
      if (status === 'trial' && trialGenerationsLeft <= 0) {
        const error = new Error("You've used all your free trial generations. Purchase an access pass to continue.");
        error.isSubscriptionError = true;
        error.subscriptionStatus = 'trial';
        throw error;
      }
      if (status === 'expired') {
        const error = new Error("Your access pass has expired. Purchase a new pass to continue.");
        error.isSubscriptionError = true;
        error.subscriptionStatus = 'expired';
        throw error;
      }
    } catch (checkError) {
      if (checkError.isSubscriptionError) {
        throw checkError;
      }
    }
    const response = await proxyapi.post('/api/v1/llm/clarification-responses', {
      userId,
      projectId,
      promptId,
      answeredQuestions
    });
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    if (!error.isSubscriptionError) {
      console.error('Error submitting clarification responses:', error);
    }
    throw error;
  }
};

export const getSecondPromptStatus = async (promptId) => {
  try {
    const response = await proxyapi.get(`/api/v1/llm/secondprompt/status/${promptId}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error('Error fetching second prompt status:', error);
    throw error;
  }
};

export const submitRevisionRequest = async (
  userId,
  projectId,
  viewId,
  sourceVersionId,
  revisionNotes
) => {
  try {
    if (!revisionNotes.differentModel) {
      revisionNotes.differentModel = 'no';
    }
    if (!revisionNotes.flexibility) {
      revisionNotes.flexibility = 'low';
    }
    const response = await proxyapi.post("/api/v1/llm/revise", {
      userId,
      projectId,
      viewId,
      sourceVersionId,
      revisionNotes
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error("Error submitting revision request:", error);
    throw error;
  }
};

export const getRevisionStatus = async (viewId, versionId) => {
  try {
    const response = await proxyapi.get(
      `/api/v1/llm/revise/status/${viewId}/${versionId}`
    );
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error("Error fetching revision status:", error);
    throw error;
  }
};

export const analyzeWireframe = async (imageBase64, projectId) => {
  try {
    const userId = await getCurrentUserId();
    const response = await proxyapi.post('/api/v1/llm/analyze-wireframe', {
      image: imageBase64,
      projectId,
      userId
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 429) {
      throw new Error(LLM_RATE_LIMIT_MESSAGE);
    }
    console.error('Error analyzing wireframe:', error);
    throw error;
  }
};
